import { Injectable, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { map, firstValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '../auth.service.js';
import { environment } from '../../../environments/environment.js';

export interface DailyRedemptionLimits {
  daily_amount_usd: number;
  daily_number: number;
  daily_redemptions_usd_limit?: number;
  daily_redemptions_number_limit?: number;
}

export interface LifetimePurchaseLimits {
  lifetime_purchase_amount_usd: number;
  lifetime_purchase_usd_limit?: number;
}

@Injectable({
  providedIn: 'root',
})
export class AccountLimitsViewService {
  private readonly _http = inject(HttpClient);
  private readonly _auth = inject(AuthService);

  private readonly _dailyRedemptionLimits = signal<DailyRedemptionLimits | null>(null);
  private readonly _lifetimePurchaseLimits = signal<LifetimePurchaseLimits | null>(null);

  /** Readonly signal surfaces for new consumers. */
  readonly dailyRedemptionLimits = this._dailyRedemptionLimits.asReadonly();
  readonly lifetimePurchaseLimits = this._lifetimePurchaseLimits.asReadonly();

  /** Backward-compat stream shims for existing observable consumers. */
  readonly dailyRedemptionLimits$ = toObservable(this._dailyRedemptionLimits);
  readonly lifetimePurchaseLimits$ = toObservable(this._lifetimePurchaseLimits);

  async fetchDailyRedemptionLimits(): Promise<DailyRedemptionLimits> {
    const response = await firstValueFrom(
      this._http.get<DailyRedemptionLimits>(`${environment.apiUrl}/self/daily-redemptions-usd`),
    );
    this._dailyRedemptionLimits.set(response);
    return response;
  }

  async fetchLifetimePurchaseLimits(): Promise<LifetimePurchaseLimits> {
    const response = await firstValueFrom(
      this._http.get<LifetimePurchaseLimits>(`${environment.apiUrl}/self/lifetime-completed-purchases-usd`),
    );
    this._lifetimePurchaseLimits.set(response);
    return response;
  }

  // Clear cached limits when user changes
  constructor() {
    this._auth.user$.pipe(map((user) => user?.uid)).subscribe((uid) => {
      if (!uid) {
        this._dailyRedemptionLimits.set(null);
        this._lifetimePurchaseLimits.set(null);
      }
    });
  }
}
